import { CheckCircle2, LoaderCircle } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { checkDeviceEndpoint } from '../../api/devices';
import { ConnectionTest, PasswordField, type ConnectionState } from './FormControls';
import type { DeviceFormProps } from './deviceTypes';

interface ComputerFormState {
  name: string;
  ipAddress: string;
  username: string;
  password: string;
  port: string;
  domain: string;
  os: 'windows' | 'linux' | 'macos' | 'other';
  connection: 'rdp' | 'ssh' | 'winrm' | 'other';
}

const defaultPortFor: Record<ComputerFormState['connection'], string> = {
  rdp: '3389',
  ssh: '22',
  winrm: '5985',
  other: '135',
};

const initialState: ComputerFormState = {
  name: '',
  ipAddress: '',
  username: '',
  password: '',
  port: '3389',
  domain: '',
  os: 'windows',
  connection: 'rdp',
};

export function ComputerForm({ onSubmit, onCancel }: DeviceFormProps) {
  const [form, setForm] = useState<ComputerFormState>(initialState);
  const [error, setError] = useState('');
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [connectionMessage, setConnectionMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const update = <Key extends keyof ComputerFormState>(key: Key, value: ComputerFormState[Key]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setConnectionState('idle');
    setConnectionMessage('');
    setError('');
  };

  const updateConnection = (connection: ComputerFormState['connection']) => {
    setForm((current) => ({ ...current, connection, port: defaultPortFor[connection] }));
    setConnectionState('idle');
    setConnectionMessage('');
    setError('');
  };

  const validate = () => {
    if (!form.name.trim()) { setError('Укажите имя компьютера.'); return false; }
    if (!form.ipAddress.trim()) { setError('Укажите IP-адрес или имя хоста.'); return false; }
    if (Number(form.port) < 1 || Number(form.port) > 65535) { setError('Порт должен быть от 1 до 65535.'); return false; }
    return true;
  };

  const handleConnectionCheck = async () => {
    setError('');
    if (!validate()) return;
    setConnectionState('checking');
    setConnectionMessage('');
    try {
      // Pre-create probe: TCP reachability of the selected management port.
      const result = await checkDeviceEndpoint({
        ipAddress: form.ipAddress.trim(),
        port: Number(form.port),
      });
      setConnectionState(result.reachable ? 'success' : 'error');
      setConnectionMessage(
        result.reachable
          ? `Порт ${form.port} отвечает за ${Math.max(1, Math.round(result.durationMs))} мс.`
          : result.message || `Порт ${form.port} недоступен. Проверьте адрес и брандмауэр.`,
      );
    } catch (checkError) {
      setConnectionState('error');
      setConnectionMessage(checkError instanceof Error ? checkError.message : 'Не удалось выполнить проверку.');
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const osLabel = { windows: 'Windows', linux: 'Linux', macos: 'macOS', other: 'Other' }[form.os];
      await onSubmit({
        deviceType: 'Computer',
        name: form.name.trim(),
        ipAddress: form.ipAddress.trim(),
        port: Number(form.port),
        osVersion: [osLabel, form.domain.trim() && form.os === 'windows' ? `(${form.domain.trim()})` : ''].filter(Boolean).join(' ').trim() || undefined,
      });
      onCancel();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Не удалось добавить компьютер.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="camera-form" onSubmit={handleSubmit}>
      <div className="camera-form__scroller">
        <div className="camera-form__primary">
          <label className="field-group">
            <span>Имя компьютера</span>
            <input required type="text" maxLength={200} value={form.name} onChange={(event) => update('name', event.target.value)} placeholder="Например, ПК-бухгалтерия" />
          </label>
          <label className="field-group">
            <span>IP-адрес / хост</span>
            <input required type="text" inputMode="url" value={form.ipAddress} onChange={(event) => update('ipAddress', event.target.value)} placeholder="192.168.1.50" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
          </label>
          <label className="field-group">
            <span>Операционная система</span>
            <select value={form.os} onChange={(event) => update('os', event.target.value as ComputerFormState['os'])}>
              <option value="windows">Windows</option>
              <option value="linux">Linux</option>
              <option value="macos">macOS</option>
              <option value="other">Другая</option>
            </select>
          </label>
          <label className="field-group">
            <span>Подключение</span>
            <select value={form.connection} onChange={(event) => updateConnection(event.target.value as ComputerFormState['connection'])}>
              <option value="rdp">RDP</option>
              <option value="ssh">SSH</option>
              <option value="winrm">WinRM</option>
              <option value="other">Другое</option>
            </select>
          </label>
          <label className="field-group">
            <span>Порт</span>
            <input required type="number" min="1" max="65535" inputMode="numeric" value={form.port} onChange={(event) => update('port', event.target.value)} />
          </label>
          <label className="field-group">
            <span>Логин</span>
            <input type="text" value={form.username} onChange={(event) => update('username', event.target.value)} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder="administrator" />
          </label>
          <PasswordField label="Пароль" value={form.password} onChange={(value) => update('password', value)} />
          {form.os === 'windows' && (
            <label className="field-group">
              <span>Домен</span>
              <input type="text" value={form.domain} onChange={(event) => update('domain', event.target.value)} placeholder="CORP" autoCapitalize="characters" autoCorrect="off" spellCheck={false} />
            </label>
          )}
        </div>

        <button className="button button--secondary camera-form__check" type="button" onClick={() => void handleConnectionCheck()} disabled={connectionState === 'checking'}>
          {connectionState === 'checking' ? <LoaderCircle className="spin" size={18} /> : <CheckCircle2 size={18} />}
          {connectionState === 'checking' ? 'Проверяем…' : 'Проверить подключение'}
        </button>

        <ConnectionTest
          state={connectionState}
          successTitle="Компьютер доступен"
          successMessage={connectionMessage}
          errorTitle="Не удалось подключиться"
          errorMessage={connectionMessage}
        />

        <p className="camera-form__security-note">Доступность компьютера проверяется по сети. Системные метрики без агента не собираются.</p>
        {error && <p className="camera-form__error" role="alert">{error}</p>}
      </div>
      <footer className="camera-dialog__actions">
        <button className="button button--secondary" type="button" onClick={onCancel}>Отмена</button>
        <button className="button button--primary" type="submit" disabled={isSubmitting}>{isSubmitting ? 'Добавление…' : 'Добавить компьютер'}</button>
      </footer>
    </form>
  );
}
